<template>
  <v-dialog v-model="isOpen" :max-width="step === 'review' ? 1100 : 733" persistent scrollable>
    <v-card>
      <v-card-title>
        {{ step === "review" ? $t("dangerousGoods.sdsImport.reviewTitle") : $t("dangerousGoods.sdsImport.title") }}
      </v-card-title>

      <v-card-text v-if="step === 'upload'">
        <v-file-input
          v-model="files"
          accept="application/pdf,.pdf"
          multiple
          outlined
          show-size
          counter
          chips
          prepend-icon="mdi-file-document-outline"
          :disabled="running"
          :label="$t('dangerousGoods.sdsImport.fileLabel')"
          :error-messages="fileError"
          @change="onFilesChanged"
        />

        <template v-if="running">
          <v-progress-linear :value="progress" height="8" rounded color="primary" class="mb-2" />
          <div class="caption grey--text text--darken-1">
            {{ $t("dangerousGoods.sdsImport.progress", { done: done, total: total }) }}
          </div>
        </template>

        <v-alert v-if="failures.length" type="warning" text class="mb-0 mt-2">
          <div>{{ $t("dangerousGoods.sdsImport.failed", { count: failures.length }) }}</div>
          <div v-for="failure in failures" :key="failure.name" class="caption mt-1">
            {{ failure.name }} — {{ failure.message }}
          </div>
        </v-alert>
      </v-card-text>

      <v-card-text v-else class="sds-review">
        <div class="text-body-2 mb-3">{{ $t("dangerousGoods.sdsImport.reviewHint") }}</div>

        <v-alert v-if="failures.length" type="warning" text dense class="mb-3">
          <div>{{ $t("dangerousGoods.sdsImport.failed", { count: failures.length }) }}</div>
          <div v-for="failure in failures" :key="failure.name" class="caption mt-1">
            {{ failure.name }} — {{ failure.message }}
          </div>
        </v-alert>

        <v-card
          v-for="row in rows"
          :key="row.key"
          outlined
          class="sds-review__row mb-3"
        >
          <div class="sds-review__head">
            <v-checkbox v-model="row.selected" hide-details class="mt-0 pt-0 mr-1" />
            <v-icon small class="mr-1">mdi-file-pdf-box</v-icon>
            <span class="sds-review__file">{{ row.displayName }}</span>
            <v-chip v-if="row.fss7Relevant" x-small color="error" outlined class="ml-2">
              {{ $t("dangerousGoods.sdsImport.fss7") }}
            </v-chip>
            <v-spacer />
            <div class="sds-review__pictograms">
              <v-tooltip v-for="path in row.pictograms" :key="path" bottom>
                <template v-slot:activator="{ on }">
                  <img :src="path" class="sds-review__pictogram" v-on="on" />
                </template>
                <span>{{ pictogramName(path) }}</span>
              </v-tooltip>
            </div>
          </div>

          <v-alert v-if="row.duplicate" type="error" text dense class="sds-review__alert">
            {{ $t("dangerousGoods.sdsImport.duplicate", { identifier: row.duplicate.identifier }) }}
          </v-alert>

          <div class="sds-review__body">
            <v-row dense>
              <v-col cols="4" sm="2">
                <v-text-field v-model="row.identifier" dense outlined hide-details :label="$t('dangerousGoods.dangerousGoodsList.tableHeader.identifier')" />
              </v-col>
              <v-col cols="8" sm="6">
                <v-text-field v-model="row.name" dense outlined hide-details :label="$t('dangerousGoods.dangerousGoodsList.form.name')" />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="row.manufacturer" dense outlined hide-details :label="$t('dangerousGoods.dangerousGoodsList.form.manufacturer')" />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="6" sm="3">
                <v-select v-model="row.storageClassification" :items="storageClassifications" item-text="name" item-value="value" dense outlined hide-details clearable :label="$t('dangerousGoods.dangerousGoodsList.form.storageClassification')" />
              </v-col>
              <v-col cols="6" sm="3">
                <v-select v-model="row.waterHazardClass" :items="waterHazardClasses" item-text="name" item-value="value" dense outlined hide-details clearable :label="$t('dangerousGoods.dangerousGoodsList.form.waterHazardClass')" />
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field v-model="row.safetyDataSheetDate" type="date" dense outlined hide-details :label="$t('dangerousGoods.sdsImport.sdsDate')" />
              </v-col>
              <v-col cols="6" sm="3" class="d-flex align-center">
                <v-checkbox v-model="row.fss7Relevant" dense hide-details class="mt-0 pt-0" :label="$t('dangerousGoods.sdsImport.fss7')" />
              </v-col>
            </v-row>

            <div class="sds-review__codes">
              <span class="sds-review__codes-label">H</span>
              <div class="sds-review__chips">
                <v-chip v-for="code in row.h" :key="'h' + code" x-small label class="mr-1 mb-1" color="orange lighten-4">{{ code }}</v-chip>
                <span v-if="!row.h.length" class="caption grey--text">–</span>
              </div>
            </div>
            <div class="sds-review__codes">
              <span class="sds-review__codes-label">P</span>
              <div class="sds-review__chips">
                <v-chip v-for="code in row.p" :key="'p' + code" x-small label class="mr-1 mb-1" color="blue-grey lighten-4">{{ code }}</v-chip>
                <span v-if="!row.p.length" class="caption grey--text">–</span>
              </div>
            </div>

            <div v-if="row.articleNumber" class="caption grey--text text--darken-1 mt-1">
              {{ $t("dangerousGoods.sdsImport.articleNumber", { value: row.articleNumber }) }}
            </div>
            <div v-if="row.warnings.length" class="mt-2">
              <div v-for="(warning, idx) in row.warnings" :key="idx" class="caption d-flex align-start">
                <v-icon x-small color="warning" class="mr-1 mt-1">mdi-alert</v-icon>
                <span>{{ warning }}</span>
              </div>
            </div>
          </div>
        </v-card>
      </v-card-text>

      <v-card-actions>
        <span v-if="step === 'review'" class="caption grey--text ml-2">
          {{ $t("dangerousGoods.sdsImport.selectedCount", { count: selectedRows.length, total: rows.length }) }}
        </span>
        <v-spacer />
        <v-btn v-if="step === 'review'" text :disabled="creating" @click="step = 'upload'">
          {{ $t("dangerousGoods.sdsImport.back") }}
        </v-btn>
        <v-btn text :disabled="running || creating" @click="close">
          {{ $t("general.close") }}
        </v-btn>
        <v-btn v-if="step === 'upload'" color="primary" :loading="running" :disabled="!validFiles.length" @click="start">
          {{ $t("dangerousGoods.sdsImport.start") }}
        </v-btn>
        <v-btn v-else color="primary" :loading="creating" :disabled="!selectedRows.length" @click="createSelected">
          {{ $t("dangerousGoods.sdsImport.create", { count: selectedRows.length }) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import { WATER_HAZARD_CLASS_ITEMS } from "../../data/waterHazardClasses";

const classifications = require("../../assets/dangerousGoodsStorageClassification.json");
const STORAGE_CLASSIFICATION_ITEMS = Object.keys(classifications).map((key) => ({
  name: key,
  value: key,
}));

const MAX_FILES = 50;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const CONCURRENCY = 2;
const SEQUENCE_NUMBER = /^\d{1,3}$/;

function cleanFileName(fileName) {
  return String(fileName || "")
    .replace(/(\.pdf_[0-9a-f-]{36})+\.pdf$/i, "")
    .replace(/\.pdf$/i, "");
}

function normalizeKey(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9äöüß]+/g, " ").trim();
}

export default {
  name: "DangerousGoodSdsImportDialog",
  props: {
    storageLocationId: { type: String, required: true },
  },
  data: () => ({
    isOpen: false,
    step: "upload",
    files: [],
    fileError: "",
    running: false,
    creating: false,
    done: 0,
    total: 0,
    created: 0,
    failures: [],
    rows: [],
    existing: [],
    nextNumber: 1,
    storageClassifications: STORAGE_CLASSIFICATION_ITEMS,
    waterHazardClasses: WATER_HAZARD_CLASS_ITEMS,
  }),
  computed: {
    ...mapGetters(["allPictograms"]),
    validFiles() {
      return this.files.filter(
        (file) => file.name.toLowerCase().endsWith(".pdf") && file.size <= MAX_FILE_BYTES
      );
    },
    progress() {
      if (!this.total) return 0;
      return Math.round((this.done / this.total) * 100);
    },
    selectedRows() {
      return this.rows.filter((row) => row.selected);
    },
  },
  methods: {
    failureMessage(error) {
      const reason = error.response?.data?.reason;
      const key = reason && `dangerousGoods.sdsImport.errors.${reason}`;
      if (key && this.$te(key)) return this.$t(key);
      if (!error.response) return this.$t("dangerousGoods.sdsImport.errors.unexpected");
      if (error.response.status >= 500) return this.$t("dangerousGoods.sdsImport.errors.unexpected");
      return (
        error.response?.data?.message ||
        error.message ||
        this.$t("dangerousGoods.sdsImport.unknownError")
      );
    },
    pictogramName(path) {
      const match = (this.allPictograms || []).find((item) => item.image === path);
      return match ? this.$t(match.text) : path;
    },
    open() {
      this.reset();
      this.isOpen = true;
    },
    close() {
      if (this.running || this.creating) return;
      this.isOpen = false;
      if (this.created) this.$emit("imported", this.created);
    },
    reset() {
      this.step = "upload";
      this.files = [];
      this.fileError = "";
      this.running = false;
      this.creating = false;
      this.done = 0;
      this.total = 0;
      this.created = 0;
      this.failures = [];
      this.rows = [];
      this.existing = [];
      this.nextNumber = 1;
    },
    async loadExisting() {
      try {
        this.existing = await this.$store.dispatch("getClientDangerousGoodSummaries");
      } catch (error) {
        this.existing = [];
      }
      const highest = this.existing.reduce((max, item) => {
        const text = String(item.identifier || "").trim();
        if (!SEQUENCE_NUMBER.test(text)) return max;
        const parsed = parseInt(text, 10);
        return parsed > max ? parsed : max;
      }, 0);
      this.nextNumber = highest + 1;
    },
    claimIdentifier() {
      const number = this.nextNumber;
      this.nextNumber += 1;
      return String(number).padStart(2, "0");
    },
    findDuplicate(name, manufacturer) {
      const nameKey = normalizeKey(name);
      if (!nameKey) return null;
      const makerKey = normalizeKey(manufacturer);
      return (
        this.existing.find(
          (item) =>
            normalizeKey(item.name) === nameKey &&
            (!makerKey || !item.manufacturer || normalizeKey(item.manufacturer) === makerKey)
        ) || null
      );
    },
    onFilesChanged(files) {
      this.fileError = "";
      this.failures = [];
      const selected = Array.isArray(files) ? files : [];
      if (selected.length > MAX_FILES) {
        this.fileError = this.$t("dangerousGoods.sdsImport.tooManyFiles", { max: MAX_FILES });
        return;
      }
      if (selected.some((file) => !file.name.toLowerCase().endsWith(".pdf"))) {
        this.fileError = this.$t("dangerousGoods.sdsImport.pdfOnly");
        return;
      }
      if (selected.some((file) => file.size > MAX_FILE_BYTES)) {
        this.fileError = this.$t("dangerousGoods.sdsImport.fileTooLarge");
      }
    },
    async start() {
      const queue = this.validFiles.slice();
      if (!queue.length) return;

      this.running = true;
      this.done = 0;
      this.total = queue.length;
      this.failures = [];
      this.rows = [];

      await this.loadExisting();

      try {
        await this.runWithConcurrency(queue, CONCURRENCY, (file) => this.analyseFile(file));
      } finally {
        this.running = false;
        this.files = [];
      }

      this.rows.sort((a, b) => a.order - b.order);
      this.rows.forEach((row) => {
        row.identifier = this.claimIdentifier();
      });
      if (this.rows.length) this.step = "review";
    },
    async analyseFile(file) {
      const order = this.done;
      try {
        const content = await this.readAsBase64(file);
        const extraction = await this.$store.dispatch("extractDangerousGoodFromSds", {
          name: file.name,
          content,
        });
        if (!extraction.name && !extraction.identifier) {
          throw new Error(this.$t("dangerousGoods.sdsImport.noData"));
        }
        const name = extraction.name || extraction.identifier;
        const duplicate = this.findDuplicate(name, extraction.manufacturer);
        this.rows.push({
          key: file.name + "-" + order,
          order,
          fileName: file.name,
          displayName: cleanFileName(file.name),
          content,
          selected: !duplicate,
          duplicate,
          identifier: "",
          articleNumber: extraction.identifier || "",
          name,
          manufacturer: extraction.manufacturer || "",
          storageClassification: extraction.storageClassification || null,
          waterHazardClass: extraction.waterHazardClass || null,
          safetyDataSheetDate: extraction.sdsDate || "",
          fss7Relevant: !!extraction.fss7Relevant,
          pictograms: extraction.pictograms || [],
          h: extraction.h || [],
          p: extraction.p || [],
          warnings: extraction.warnings || [],
        });
      } catch (error) {
        this.failures.push({ name: file.name, message: this.failureMessage(error) });
      } finally {
        this.done += 1;
      }
    },
    async createSelected() {
      this.creating = true;
      const remaining = [];
      try {
        for (const row of this.rows) {
          if (!row.selected) {
            remaining.push(row);
            continue;
          }
          try {
            await this.$store.dispatch("importDangerousGoodFromSds", {
              identifier: String(row.identifier || "").trim(),
              name: String(row.name || "").trim(),
              manufacturer: String(row.manufacturer || "").trim(),
              storageClassification: row.storageClassification || null,
              waterHazardClass: row.waterHazardClass || null,
              safetyDataSheetDate: row.safetyDataSheetDate || null,
              fss7Relevant: !!row.fss7Relevant,
              pictograms: row.pictograms,
              h: row.h,
              p: row.p,
              dangerousGoodStorageLocation: this.storageLocationId,
              safetyDataSheet: {
                name: cleanFileName(row.fileName),
                filetype: "pdf",
                content: row.content,
              },
            });
            this.created += 1;
          } catch (error) {
            row.warnings = [
              this.$t("dangerousGoods.sdsImport.errorFile") +
                ": " +
                (error.response?.data?.message || error.message || this.$t("dangerousGoods.sdsImport.unknownError")),
              ...row.warnings,
            ];
            remaining.push(row);
          }
        }
      } finally {
        this.creating = false;
      }
      this.rows = remaining;
      if (!this.rows.length) {
        this.$store.commit(
          "CREATE_SNACKBAR",
          this.$t("dangerousGoods.sdsImport.created", { count: this.created })
        );
        this.close();
      }
    },
    async runWithConcurrency(items, concurrency, handler) {
      let nextIndex = 0;
      const worker = async () => {
        while (nextIndex < items.length) {
          const item = items[nextIndex];
          nextIndex += 1;
          await handler(item);
        }
      };
      await Promise.all(
        Array.from({ length: Math.min(concurrency, items.length) }, () => worker())
      );
    },
    readAsBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const value = event.target && event.target.result;
          if (typeof value !== "string" || !value.includes(",")) {
            reject(new Error(this.$t("dangerousGoods.sdsImport.readError")));
            return;
          }
          resolve(value.split(",")[1]);
        };
        reader.onerror = () => reject(new Error(this.$t("dangerousGoods.sdsImport.readError")));
        reader.readAsDataURL(file);
      });
    },
  },
};
</script>

<style scoped>
.sds-review__row {
  border-radius: 8px;
}
.sds-review__head {
  display: flex;
  align-items: center;
  padding: 10px 14px 6px;
  min-height: 52px;
}
.sds-review__file {
  font-weight: 600;
  font-size: 0.95rem;
}
.sds-review__pictograms {
  display: flex;
  align-items: center;
  gap: 6px;
}
.sds-review__pictogram {
  width: 36px;
  height: 36px;
  display: block;
}
.sds-review__alert {
  margin: 4px 14px 8px;
}
.sds-review__body {
  padding: 4px 14px 12px;
}
.sds-review__codes {
  display: flex;
  align-items: flex-start;
  margin-top: 10px;
}
.sds-review__codes-label {
  min-width: 22px;
  margin-top: 2px;
  font-weight: 600;
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
}
.sds-review__chips {
  display: flex;
  flex-wrap: wrap;
}
</style>
